import { apiClient } from '@/api/client';
import type { LocalizationResponse, LanguageInfo } from './types';

const STORAGE_KEY = 'motr_language';
const DEFAULT_CULTURE = 'en';
const DEFAULT_RESOURCE = 'motr';

// Cache for languages (doesn't change often)
let cachedLanguages: LanguageInfo[] | null = null;

/**
 * Get the current language from storage or browser
 */
export const getCurrentLanguage = (): string => {
  // Check localStorage first
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return stored;

  // Fall back to browser language
  const browserLang = navigator.language.split('-')[0];
  return browserLang || DEFAULT_CULTURE;
};

/**
 * Save language preference
 */
export const setCurrentLanguage = (cultureName: string): void => {
  localStorage.setItem(STORAGE_KEY, cultureName);
};

/**
 * Fetch available languages from ABP application configuration
 */
export const fetchLanguages = async (): Promise<LanguageInfo[]> => {
  // Return cached languages if available
  if (cachedLanguages) {
    return cachedLanguages;
  }

  try {
    const response = await apiClient.get<{
      localization: {
        languages: LanguageInfo[];
        currentCulture: { cultureName: string };
      };
    }>('/api/abp/application-configuration', {
      params: {
        includeLocalizationResources: false,
      },
    });

    cachedLanguages = response.data.localization?.languages || [];
    return cachedLanguages;
  } catch (error) {
    console.error('[i18n] Failed to fetch languages:', error);
    return [];
  }
};

/**
 * Fetch localization data from ABP API
 * Uses the same endpoint as Angular: /api/abp/application-localization
 */
export const fetchLocalizations = async (
  cultureName: string
): Promise<LocalizationResponse> => {
  // Fetch both translations and languages in parallel
  const [localizationResponse, languages] = await Promise.all([
    apiClient.get<LocalizationResponse>('/api/abp/application-localization', {
      params: {
        cultureName,
        onlyDynamics: false,
      },
    }),
    fetchLanguages(),
  ]);

  // Merge languages into the response
  return {
    ...localizationResponse.data,
    languages,
  };
};

/**
 * Flatten all resources into a single translations object
 *
 * Supports multiple key formats:
 * - 'Dashboard' -> looks in default resource (motr)
 * - 'motr::Dashboard' -> looks in motr resource
 * - 'AbpIdentity::Users' -> looks in AbpIdentity resource
 * - 'Menu:Dashboard' -> looks for 'Menu:Dashboard' key in default resource
 *
 * This mirrors ABP's localization behavior where:
 * - Keys without '::' use the default resource
 * - Keys with '::' specify the resource explicitly
 */
export const flattenTranslations = (
  resources: Record<string, { texts: Record<string, string> }>
): Record<string, string> => {
  const translations: Record<string, string> = {};

  for (const [resourceName, resource] of Object.entries(resources)) {
    if (!resource.texts) continue;

    for (const [key, value] of Object.entries(resource.texts)) {
      // Store with full resource::key format
      translations[`${resourceName}::${key}`] = value;

      // For the default resource (motr), also store without prefix
      // This allows using just 'Dashboard' instead of 'motr::Dashboard'
      if (resourceName === DEFAULT_RESOURCE) {
        translations[key] = value;
      }
    }
  }

  return translations;
};

/**
 * Interpolate parameters into a translation string
 * Supports both {paramName} and {{paramName}} syntax for ABP compatibility
 */
export const interpolate = (
  text: string,
  params?: Record<string, string | number>
): string => {
  if (!params) return text;

  // Support both {param} and {{param}} formats
  return text
    .replace(/\{\{(\w+)\}\}/g, (match, paramName) => {
      const value = params[paramName];
      return value !== undefined ? String(value) : match;
    })
    .replace(/\{(\w+)\}/g, (match, paramName) => {
      const value = params[paramName];
      return value !== undefined ? String(value) : match;
    });
};

/**
 * Resolve a translation key to the actual lookup key
 *
 * Examples:
 * - 'Dashboard' -> 'Dashboard' (uses default resource)
 * - 'motr::Dashboard' -> 'motr::Dashboard'
 * - '::Dashboard' -> 'motr::Dashboard' (:: prefix means default resource)
 */
export const resolveKey = (key: string): string => {
  // Handle '::key' format (short for default resource)
  if (key.startsWith('::')) {
    return `${DEFAULT_RESOURCE}${key}`;
  }
  return key;
};
